import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../Redux/store";
import { Bid } from "../../../Types/Bid";

const PAGE_SIZE = 25;

interface FetchBidArgs {
    lookup?: string;
    page?: number;
    sortBy?: 'quoteID' | 'created' | 'expires' | null | undefined;
    sortDirection?: 'asc' | 'desc' | null | undefined;
    writerId?: string | null;
};

export interface BidResponse {
    data: Bid[];
    page: number;
    pageSize: number;
    totalBids: number
};

export const getBids = createAsyncThunk<
    BidResponse, 
    FetchBidArgs, 
    { state: RootState }
    >('bids/getBids',
    async(args, { getState, rejectWithValue, }) => {
        try {
            const token = getState().auth.token
            const params = new URLSearchParams();

            params.append('page', String(args?.page ?? 1));

            if (args?.sortBy) params.append('sortBy', args.sortBy);
            if (args?.sortDirection) params.append('sortDirection', args.sortDirection);
            if (args?.lookup) params.append('lookup', args.lookup);
            if (args?.writerId) params.append('writerId', args.writerId);

            const bidUrl = `https://customer.hirsch.com/bids/v1?${params.toString()}`
            
            const res = await fetch(bidUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': btoa(`Bearer ${token}`)
                },
            })
            const json = await res.json()

            if (!json.ack) {
                return rejectWithValue(json.errorMessage);
            }
            // console.log('BIDS:', json)
            return { 
                data: json.data, 
                page: args?.page ?? 1,
                totalBids: json.total,
                pageSize: json.totalPages,
            }
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const getBidDetails = createAsyncThunk<any[], string, { state: RootState }>(
    'bids/getBidDetails',
    async(key, { getState, rejectWithValue, }) => {
        try {
            const token = getState().auth.token
            const bidUrl = `https://customer.hirsch.com/bids/v2/details?id=${key}`
            
            const res = await fetch(bidUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': btoa(`Bearer ${token}`)
                },
            })
            const json = await res.json()

            if (!json.ack) {
                return rejectWithValue(json.errorMessage);
            }
            console.log('Get Bid Details Error:', json.errorMessage);
            console.log('Bid Details:', json.data[0].products)
            return json.data
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

interface BidState {
    bidsArr: any[];
    bidQueryArr: Bid[];
    bidDetailsArr: any[];
    page: number;
    currentRequestPage: number;
    totalBids: number;
    hasMore: boolean;
    loading: boolean;
    error: string | boolean | null | undefined;
};

const initialState: BidState = {
    bidsArr: [],
    bidQueryArr: [],
    bidDetailsArr: [],
    page: 1,
    currentRequestPage: 1,
    totalBids: 0,
    hasMore: true,
    loading: false,
    error: null,
};

const bidSlice = createSlice({
    name: 'bids',
    initialState,
    reducers: {
        setBidError: (state, action) => {
            state.error = action.payload;
        },
        deleteBid: (state, action) => {
            state.bidsArr = state.bidsArr.filter(bid => bid.quoteID !== action.payload)
        },
        setBidQueryArr: (state, action) => {
            state.bidQueryArr = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
        .addCase(getBids.pending, (state, action) => {
            state.loading = true;
            state.currentRequestPage = action.meta.arg.page ?? 1;
            state.error = null;
        })
        .addCase(getBids.fulfilled, (state, action) => {
            state.loading = false;

            const lookup = action.meta.arg?.lookup;
            state.currentRequestPage = action.payload.page

            if (lookup) {
                if (action.payload.page === 1) {
                    state.bidQueryArr = action.payload.data
                    state.error = false;
                } else {
                    state.bidQueryArr.push(...action.payload.data)
                }
            };

            
            if (action.payload.page === 1) {
                state.bidsArr = action.payload.data;
            } else {
                state.bidsArr.push(...action.payload.data);
            }
            
            state.totalBids = action.payload.totalBids;
            state.page = action.payload.page;
            state.hasMore = action.payload.page < action.payload.pageSize;
            state.error = false;
        })
        .addCase(getBids.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error ? action.error.message : 'Failed to Fetch'
        })
        .addCase(getBidDetails.pending, (state) => {
            state.loading = true;
            state.error = false;
        })
        .addCase(getBidDetails.fulfilled, (state, action) => {
            state.loading = false;
            state.bidDetailsArr = action.payload
            state.error = false;
        })
        .addCase(getBidDetails.rejected, (state, action) => {
            state.loading = false
            state.error = action.error ? action.error.message : 'Failed to Fetch';
        })
    }
})

export const bidsReducer = bidSlice.reducer;

export const {
    setBidError,
    deleteBid,
    setBidQueryArr
} = bidSlice.actions;

export const getBidsList = (state: RootState) => 
    state.bids.bidsArr;
export const getBidQueryList = (state: RootState) =>
    state.bids.bidQueryArr;
export const getBidsPage = (state: RootState) =>
    state.bids.page;
export const getHasMoreBids = (state: RootState) =>
    state.bids.hasMore;
export const getBidsLoading = (state: RootState) =>
    state.bids.loading;
export const getBidsRequestPage = (state: RootState) =>
    state.bids.currentRequestPage;
export const getTotalBids = (state: RootState) =>
    state.bids.totalBids;

export const getBidDetail = (state: RootState) => {
    return state.bids.bidDetailsArr;
}